import {createCarousel} from "../carousel";
import {isMobile} from "../../modules/devices";
import {customDocumentEvent, documentLoadEvent} from "../../modules/events";
import {registerProductRecommenderInit} from "../product-page-recommender-bridge";

function alignRecommenderButtons(root, prevButton, nextButton)
{
    if (!root || (!prevButton && !nextButton)) return;

    const setTop = function() {
        const image = root.querySelector('.recommender-carousel__slide img');
        if (!image) return;
        const imageHeight = image.offsetHeight;
        if (!imageHeight) return;
        const arrowTop = imageHeight / 2 + 'px';
        if (prevButton) prevButton.style.top = arrowTop;
        if (nextButton) nextButton.style.top = arrowTop;
    };

    root._alignRecommenderButtons = setTop;
    setTop();

    const image = root.querySelector('.recommender-carousel__slide img');
    if (image && (!image.complete || image.naturalWidth === 0)) {
        image.addEventListener('load', setTop, {once: true});
        image.addEventListener('error', setTop, {once: true});
    }

    if (!root._recommenderArrowResizeBound) {
        root._recommenderArrowResizeBound = true;
        window.addEventListener('resize', setTop);
    }
}

function loadSlideImage(slide)
{
    if (!slide || slide.dataset.lazyLoaded === '1') return;
    const img = slide.querySelector('img[data-src]');
    if (!img) {
        slide.dataset.lazyLoaded = '1';
        return;
    }
    const loader = slide.querySelector('.loader');
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
    const hideLoader = function() {
        if (loader) loader.classList.add('d-none');
        slide.dataset.lazyLoaded = '1';
        const root = slide.closest('.recommender-carousel');
        if (root && typeof root._alignRecommenderButtons === 'function') {
            root._alignRecommenderButtons();
        }
    };
    if (img.complete && img.naturalWidth > 0) {
        hideLoader();
        return;
    }
    img.addEventListener('load', hideLoader, {once: true});
    img.addEventListener('error', hideLoader, {once: true});
}

function loadVisibleRecommenderImages(root)
{
    const viewport = root.querySelector('.recommender-carousel__viewport') || root;
    const viewportRect = viewport.getBoundingClientRect();
    root.querySelectorAll('.recommender-carousel__slide').forEach(function(slide) {
        const rect = slide.getBoundingClientRect();
        const visible = rect.left < viewportRect.right + 40 && rect.right > viewportRect.left - 40;
        if (visible) loadSlideImage(slide);
    });
}

function observeRecommenderLazyImages(root)
{
    const slides = root.querySelectorAll('.recommender-carousel__slide img[data-src]');
    if (!slides.length) return;
    if (!('IntersectionObserver' in window)) {
        root.querySelectorAll('.recommender-carousel__slide').forEach(loadSlideImage);
        return;
    }
    const viewport = root.querySelector('.recommender-carousel__viewport') || root;
    const observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
            if (!entry.isIntersecting) return;
            loadSlideImage(entry.target.closest('.recommender-carousel__slide'));
            observer.unobserve(entry.target);
        });
    }, {root: viewport, rootMargin: '40px', threshold: 0.01});
    slides.forEach(function(img) { observer.observe(img); });
}

function getVisibleRecommenderSlides(root)
{
    const viewport = root.querySelector('.recommender-carousel__viewport') || root;
    const slide = root.querySelector('.recommender-carousel__slide');
    if (!slide) return 1;
    const slideWidth = slide.offsetWidth;
    if (slideWidth <= 0) return 1;
    const marginRight = parseFloat(window.getComputedStyle(slide).marginRight) || 0;
    const stride = slideWidth + marginRight;
    if (stride <= 0) return 1;
    return Math.max(1, Math.round(viewport.offsetWidth / stride));
}

export function initNativeRecommender(recommender)
{
    if (!recommender) return;
    const root = recommender.closest('.recommender-carousel');
    if (!root || root.classList.contains('is-initialized')) return;

    const prevButton = root.querySelector('.recommender-carousel__prev');
    const nextButton = root.querySelector('.recommender-carousel__next');
    const api = createCarousel(root, {
        selectors: {
            viewport: '.recommender-carousel__viewport',
            track: '.recommender-carousel__track',
            slide: '.recommender-carousel__slide',
            prev: '.recommender-carousel__prev',
            next: '.recommender-carousel__next'
        },
        loop: false,
        mode: 'offset',
        slidesPerView: 1,
        slidesPerGroup: function() {
            return getVisibleRecommenderSlides(root);
        },
        startIndex: 0,
        swipe: isMobile(),
        suppressClickOnSwipe: true,
        arrows: {mode: 'disable'},
        watchResize: true,
        onSelect: function() {
            loadVisibleRecommenderImages(root);
            alignRecommenderButtons(root, prevButton, nextButton);
        }
    });
    if (!api) return;
    alignRecommenderButtons(root, prevButton, nextButton);
    loadVisibleRecommenderImages(root);
    observeRecommenderLazyImages(root);
}

export function bootstrapRecommenderType(type)
{
    const initMatching = function(root) {
        const scope = root || document;
        scope.querySelectorAll('.recommender[data-type="' + type + '"]').forEach(function(recommender) {
            if (recommender.closest('.recommender-carousel')) {
                initNativeRecommender(recommender);
            }
        });
    };

    registerProductRecommenderInit(function(recommender) {
        if (recommender && recommender.getAttribute('data-type') === type) {
            initNativeRecommender(recommender);
        }
    });

    customDocumentEvent('productRecommendersRendered', function(event) {
        initMatching(event.detail && event.detail.container ? event.detail.container : document);
    });

    documentLoadEvent(function() {
        initMatching(document);
    });
}
